import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import Modal from 'components/shared/Modal';
import React, { SyntheticEvent, useCallback, useContext, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { defaultFormProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { ModalProps } from 'store/types/ComponentProps';
import UserService from 'services/api/UserService';
import CompanyService from 'services/api/CompanyService';
import { useSnackbar } from 'notistack';
import JobFunction from 'store/types/JobFunction';
import CompanyUser from 'store/types/CompanyUser';
import UserNameFormSection from 'components/profile/UserNameFormSection';
import EmailFormItem from 'components/profile/EmailFormItem';
import DateFormItem from 'components/shared/DateFormItem';
import PhoneFormItem from 'components/profile/PhoneFormItem';
import FileUploadFormItem from 'components/shared/FileUploadFormItem';
import { CompanyUserFormValues } from 'store/types/FormValues';
import { ConfigContext } from 'components/ConfigGuard';
import { UserContext } from 'components/UserGuard';
import TitleFormItem from 'components/profile/TitleFormItem';

import commonStyles from 'styles/common.module.scss';
import styles from './EditCompanyUserModal.module.scss';

interface EditCompanyUserModalProps extends ModalProps {
  data: CompanyUser;
  jobFunctionsList?: JobFunction[];
  refetch: () => void;
  companyId: string;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type ViewCompanyUserFormValues = CompanyUserFormValues;

const getDefaultValues = ({
  firstName,
  lastName,
  middleInitial,
  prefix,
  suffix,
  phone,
  email,
  title,
  birthDate,
}: CompanyUser): ViewCompanyUserFormValues => ({
  firstName,
  lastName,
  middleInitial,
  prefix,
  suffix,
  phone,
  email,
  title,
  birthDate,
  functionIds: [],
  files: [],
});

const EditCompanyUserModal: React.FunctionComponent<EditCompanyUserModalProps> = ({
  open,
  onClose,
  refetch,
  data,
  companyId,
  jobFunctionsList,
}) => {
  const defaultValues = useMemo(() => getDefaultValues(data), [data]);
  const {
    modulesSettings: {
      companyManagement: { enableContactBirthdate, enableContactFileUpload },
    },
  } = useContext(ConfigContext);
  const { id } = useContext(UserContext);
  const form = useForm<ViewCompanyUserFormValues>({ ...defaultFormProps, defaultValues });
  const { handleSubmit, formState, control, reset, watch } = form;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deletedCustomerFunctionIds, setDeletedCustomerFunctionIds] = useState<string[]>([]);

  const { functionIds = [] } = watch();

  const activeCustomerFunctions = useMemo(
    () => data.functions?.filter((customerFunction) => !deletedCustomerFunctionIds.includes(customerFunction.id)) ?? [],
    [data.functions, deletedCustomerFunctionIds]
  );

  const availableJobFunctions = useMemo(() => {
    const existingJobFunctionIds = new Set(
      data.functions?.map((customerFunction) => customerFunction.functionId) ?? []
    );

    return jobFunctionsList?.filter((jobFunction) => !existingJobFunctionIds.has(jobFunction.id)) ?? [];
  }, [data.functions, jobFunctionsList]);

  const hasCustomerFunctionChanges = Boolean(functionIds.length || deletedCustomerFunctionIds.length);
  const hasUserChanges = Object.keys(formState.dirtyFields).some((fieldName) => fieldName !== 'functionIds');

  const handleClose = useCallback(() => {
    onClose();
    reset(defaultValues);
    setDeletedCustomerFunctionIds([]);
    setDeleteModalOpen(false);
  }, [defaultValues, onClose, reset]);

  const handleDeleteModalOpen = useCallback(() => {
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  const handleDeleteButtonClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      if (data) {
        setLoading(true);
        CompanyService.removeCompanyUser(companyId, data.id)
          .then(() => {
            enqueueSnackbar('Person successfully deleted', { variant: 'success' });
            setLoading(false);
            refetch();
            handleClose();
          })
          .catch((errorMessage) => {
            setLoading(false);
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          });
      }
    },
    [companyId, data, enqueueSnackbar, handleClose, refetch]
  );

  const handleUpdateUserSubmit = useCallback(
    async ({ functionIds: addedFunctionIds, files = [], ...userValues }: ViewCompanyUserFormValues) => {
      if (!data) {
        return;
      }

      setLoading(true);

      try {
        if (enableContactFileUpload && data.id && files.length > 0) {
          await UserService.uploadFile(data.id, files[0]);
        }

        await CompanyService.updateCompanyUser(
          companyId,
          data.id,
          hasUserChanges ? userValues : null,
          addedFunctionIds ?? [],
          deletedCustomerFunctionIds
        );

        enqueueSnackbar('Person successfully updated', {
          variant: 'success',
        });

        refetch();
        handleClose();
      } catch (errorMessage) {
        enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
      } finally {
        setLoading(false);
      }
    },
    [
      companyId,
      data,
      deletedCustomerFunctionIds,
      enableContactFileUpload,
      enqueueSnackbar,
      handleClose,
      hasUserChanges,
      refetch,
    ]
  );

  const handleCustomerFunctionDelete = useCallback((customerFunctionId: string) => {
    setDeletedCustomerFunctionIds((currentIds) =>
      currentIds.includes(customerFunctionId) ? currentIds : [...currentIds, customerFunctionId]
    );
  }, []);

  return (
    <>
      <FormProvider {...form}>
        <Modal
          open={open}
          onClose={handleClose}
          maxWidth={'sm'}
          title={'Edit Person'}
          loading={loading}
          actions={
            <>
              <div>
                <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
                  {'Cancel'}
                </Button>
                {id !== data?.id && (
                  <Button
                    color={'secondary'}
                    variant={'outlined'}
                    onClick={handleDeleteModalOpen}
                    className={styles.submitButton}
                  >
                    {'Remove Person'}
                  </Button>
                )}
              </div>
              <Button
                color={'secondary'}
                variant={'contained'}
                type={'submit'}
                disabled={!formState.isValid || (!hasUserChanges && !hasCustomerFunctionChanges)}
                onClick={handleSubmit(handleUpdateUserSubmit)}
              >
                {'Save'}
              </Button>
            </>
          }
        >
          <Grid {...defaultGridContainerProps}>
            {data.entityId && (
              <Grid {...defaultGridItemProps}>
                <p className={styles.id}>{`ID: ${data.entityId}`}</p>
              </Grid>
            )}
            <UserNameFormSection middleNameVisible={true} />
            <Grid {...defaultGridItemProps} md={6}>
              <EmailFormItem disabled={true} />
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <PhoneFormItem />
            </Grid>
            <Grid {...defaultGridItemProps} md={6}>
              <TitleFormItem />
            </Grid>
            {enableContactBirthdate && (
              <Grid {...defaultGridItemProps} md={6}>
                <DateFormItem
                  fieldName={'birthDate'}
                  label={'Date of Birth'}
                  maxDate={moment(new Date()).startOf('day')}
                />
              </Grid>
            )}
            {enableContactFileUpload && (
              <Grid {...defaultGridItemProps}>
                <FileUploadFormItem></FileUploadFormItem>
              </Grid>
            )}
            <Grid {...defaultGridItemProps}>
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Typography variant={'h6'} sx={{ mb: 1 }}>
                {'Manage Permissions'}
              </Typography>

              <FormLabel>{'Portal Roles'}</FormLabel>
              <Stack divider={<Divider flexItem />} sx={{ mb: 2 }}>
                {activeCustomerFunctions.length ? (
                  activeCustomerFunctions.map((customerFunction) => (
                    <Stack
                      key={customerFunction.id}
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      sx={{ minHeight: 48, py: 0.5 }}
                    >
                      <Typography>{customerFunction.functionName}</Typography>
                      {id !== data?.id && (
                        <IconButton
                          aria-label={`Remove ${customerFunction.functionName} portal role`}
                          color={'error'}
                          disabled={loading}
                          size={'small'}
                          onClick={() => handleCustomerFunctionDelete(customerFunction.id)}
                        >
                          <DeleteOutlineIcon fontSize={'small'} />
                        </IconButton>
                      )}
                    </Stack>
                  ))
                ) : (
                  <Typography color={'text.secondary'} sx={{ py: 1.5 }}>
                    {'No portal roles assigned.'}
                  </Typography>
                )}
              </Stack>

              <Controller
                name={'functionIds'}
                control={control}
                render={({ field }) => {
                  const selectedOptions = availableJobFunctions.filter(({ id }) => field.value?.includes(id));

                  return (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      disabled={loading || !availableJobFunctions.length}
                      options={availableJobFunctions}
                      value={selectedOptions}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onBlur={field.onBlur}
                      onChange={(_event, selectedRoles) => {
                        field.onChange(selectedRoles.map(({ id }) => id));
                      }}
                      ChipProps={{
                        color: 'secondary',
                        variant: 'filled',
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} checkedIcon={checkedIcon} icon={icon} sx={{ mr: 1 }} />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={field.ref}
                          label={'Add Portal Roles'}
                          placeholder={selectedOptions.length ? undefined : 'Select one or more Portal Roles'}
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Modal>
      </FormProvider>
      <Modal
        title={'Remove Person'}
        loading={loading}
        open={deleteModalOpen}
        maxWidth={'xs'}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleDeleteModalClose}>
              {'Cancel'}
            </Button>
            <Button
              type={'submit'}
              variant={'contained'}
              onClick={handleDeleteButtonClick}
              className={commonStyles.dangerButtonContained}
            >
              {'Remove'}
            </Button>
          </>
        }
      >
        <p className={commonStyles.text}>{'Are you sure you want to remove this person?'}</p>
      </Modal>
    </>
  );
};

export default EditCompanyUserModal;
